import { z } from 'zod';

import { nonNegativeIntSchema, percentageSchema } from '../common/primitives';

/**
 * Laufzeit-Messwerte eines Worker-Nodes.
 *
 * Diese Werte beschreiben die *tatsächliche* Last, nicht die vom Master
 * gebuchten Reservierungen (siehe `capacity.ts`). Beide werden im Scheduler
 * kombiniert: Reservierungen verhindern Überbuchung, Messwerte verhindern,
 * dass ein nominell freier, aber real überlasteter Node neue Sites bekommt.
 */

/** Auslastung des Docker-Daemons. */
export const dockerMetricsSchema = z.object({
  /** Docker-Server-Version, z. B. `24.0.7`. */
  serverVersion: z.string().min(1),
  /** Laufende Container (inklusive Plattform-Container wie Traefik und MariaDB). */
  containersRunning: nonNegativeIntSchema,
  /** Alle Container, auch gestoppte. */
  containersTotal: nonNegativeIntSchema,
  /** Lokal vorgehaltene Images — relevant für die Image-Affinität im Scheduler. */
  imagesTotal: nonNegativeIntSchema,
});

export type DockerMetrics = z.infer<typeof dockerMetricsSchema>;

/** Messwerte des Hostsystems. */
export const nodeMetricsSchema = z.object({
  /** CPU-Auslastung über das letzte Messintervall, 0–100. */
  cpuPercent: percentageSchema,
  /** Load Average der letzten Minute. Auf Systemen ohne Unterstützung `null`. */
  loadAverage1m: z.number().nonnegative().nullable(),
  /** Gesamter Arbeitsspeicher in Mebibyte. */
  memoryTotalMb: nonNegativeIntSchema,
  /** Belegter Arbeitsspeicher in Mebibyte. */
  memoryUsedMb: nonNegativeIntSchema,
  /**
   * Gesamter Plattenplatz des Datenverzeichnisses in Gibibyte — **der Vorrat**,
   * aus dem die Sites bedient werden.
   *
   * Seit M6b ist das Datenverzeichnis auf einem Worker ein eigenes
   * Dateisystem (ein XFS-Abbild, siehe ADR 0013). Diese Zahl bezeichnet damit
   * das Abbild und nicht mehr die Platte des Servers. Auf einem Node ohne
   * eigenen Einhängepunkt fallen beide zusammen — deshalb ist die Änderung
   * rückwärtskompatibel und deshalb steht die Wurzelplatte daneben.
   */
  diskTotalGb: nonNegativeIntSchema,
  /** Belegter Plattenplatz des Vorrats in Gibibyte. */
  diskUsedGb: nonNegativeIntSchema,
  /**
   * Gesamter Plattenplatz der Wurzelplatte in Gibibyte. Optional, weil ein
   * Agent vor M6b ihn nicht meldet.
   *
   * Er steht neben dem Vorrat und nicht an seiner Stelle: Läuft die
   * Wurzelplatte voll, während der Vorrat halb leer ist, ist das ein
   * Betriebsproblem (Logs, Images, Journal) und keine Kapazitätsfrage. Zwei
   * Zahlen machen den Unterschied **sichtbar**; eine ließe ihn vermuten.
   */
  rootDiskTotalGb: nonNegativeIntSchema.optional(),
  /** Belegter Plattenplatz der Wurzelplatte in Gibibyte. */
  rootDiskUsedGb: nonNegativeIntSchema.optional(),
  /** Laufzeit des Hostsystems in Sekunden. */
  uptimeSeconds: nonNegativeIntSchema,
  /**
   * Docker-Kennzahlen. `null`, wenn der Daemon beim Messen nicht erreichbar war
   * — der Heartbeat wird trotzdem gesendet, damit der Master zwischen
   * "Node tot" und "nur Docker tot" unterscheiden kann. Genau diese
   * Unterscheidung entscheidet, ob ein Alarm oder eine Migration die richtige
   * Reaktion ist.
   */
  docker: dockerMetricsSchema.nullable(),
});

export type NodeMetrics = z.infer<typeof nodeMetricsSchema>;

/**
 * Speicherauslastung als Anteil von 0 bis 1.
 * Gibt bei `memoryTotalMb === 0` den Wert 1 zurück (voll belegt) — ein Node
 * ohne meldbaren Speicher darf niemals als "hat viel frei" bewertet werden.
 */
export function memoryUsageRatio(metrics: NodeMetrics): number {
  if (metrics.memoryTotalMb <= 0) {
    return 1;
  }

  return Math.min(1, metrics.memoryUsedMb / metrics.memoryTotalMb);
}

/** Plattenauslastung als Anteil von 0 bis 1. Gleiche Vorsichtsregel wie oben. */
export function diskUsageRatio(metrics: NodeMetrics): number {
  if (metrics.diskTotalGb <= 0) {
    return 1;
  }

  return Math.min(1, metrics.diskUsedGb / metrics.diskTotalGb);
}
